import type { ReactElement } from "react";
import { Button } from "@ndd/ui";
import { isAccessDeniedError, isNetworkRequestError, isNotFoundError } from "@/lib/api";
import { cn } from "@/lib/utils";

export function QueryErrorState({
  error,
  notFoundText,
  onRetry,
  className,
}: {
  error: unknown;
  notFoundText: string;
  onRetry: () => void;
  className?: string;
}): ReactElement {
  const notFound = isNotFoundError(error);
  const accessDenied = isAccessDeniedError(error);
  const networkFailure = isNetworkRequestError(error);
  // 403 covers both ownership rejections and deactivated accounts; the
  // client can't tell them apart without trusting the server body, so the
  // copy stays neutral and the retry button is withheld (a retry can't
  // change an authorization outcome).
  let text = "Something went wrong";
  if (notFound) text = notFoundText;
  else if (accessDenied) text = "You don't have access to this";
  else if (networkFailure) text = "Unable to reach AlwaysAI. Check your connection and try again.";

  const canRetry = !notFound && !accessDenied;

  return (
    <div className={cn("flex flex-col items-center justify-center gap-3", className)}>
      <p className="text-muted-foreground">{text}</p>
      {canRetry && (
        <Button type="button" variant="outline" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}
